import {FaultTree} from './faultTree';

/**
 * A move along a Component's lifecycle. Named for the act, because that is what a user chooses:
 * they publish a draft, they do not set it to published. None of them can be undone.
 */
export enum ComponentLifecycleTransition {
    PUBLISH = 'PUBLISH',
    DEPRECATE = 'DEPRECATE',
    ARCHIVE = 'ARCHIVE',
}

export enum ComponentState {
    DRAFT = 'DRAFT',
    PUBLISHED = 'PUBLISHED',
    DEPRECATED = 'DEPRECATED',
    ARCHIVED = 'ARCHIVED',
}

/**
 * How big a release is, chosen by whoever publishes it: which segment of the MAJOR.MINOR.PATCH
 * version advances. The backend has no safe default for this -- publishing requires one.
 */
export enum ReleaseLevel {
    MAJOR = 'MAJOR',
    MINOR = 'MINOR',
    PATCH = 'PATCH',
}

/**
 * One report source a Component version has a mapping row for (matches the backend
 * ComponentMappedSourceDTO): the source name, whether a run against it has finished, and when.
 */
export interface ComponentMappedSource {
    sourceName: string;
    complete: boolean;
    lastRunAt?: string;
}

/**
 * List-row projection of a Component (matches the backend ComponentSummaryDTO). No fault tree.
 */
export interface ComponentSummary {
    id: number;
    uniqueId: string;
    name: string;
    description: string;
    // What changed since the previous release, captured at publish time; absent on a DRAFT.
    releaseNotes?: string;
    version?: string;              // MAJOR.MINOR.PATCH, e.g. "1.0.0"; absent while the Component is a DRAFT
    state: ComponentState;
    groupId?: number;              // the owning group; never changes, publishing included
    // The full owning group (backend GroupDTO: id / name / parent / pathToRoot / currentUser.role),
    // used to build the working-area tree in the explorer.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    group?: any;
    created?: Date;
    updated?: Date;
    /**
     * How many report sources this version has had a mapping run against, and when the most recent
     * of those runs finished. The library list's cheap readout: it says what has been run, not
     * whether it is still current, which the Mapping tab answers one source at a time.
     */
    sourcesRunAgainst?: number;
    lastRunAt?: string;
    // Every report source this version has a mapping row for, finished or not.
    mappedSources?: ComponentMappedSource[];
}

/**
 * Full Component (matches the backend ComponentDTO). Carries the interior fault tree only on
 * responses to the fault-tree editing endpoints.
 */
export interface Component extends ComponentSummary {
    faultTreeId?: number;
    faultTree?: FaultTree;
}

/**
 * One entry in a DRAFT Component's edit history (GET /api/v2/components/{id}/history). The snapshot
 * comes with it only when asked for; otherwise it is fetched on its own when the entry is opened.
 */
export interface ComponentVersion {
    id: number;
    componentId: number;
    precedingVersionId?: number;
    // Who made the change (a username).
    userId?: string;
    // What the change was, e.g. "Add node".
    action?: string;
    comment?: string;
    created: string;
    // Whether the draft currently matches this entry; undo and redo move it.
    current: boolean;
    // The Component as it was at this entry; only when the list was asked for snapshots.
    snapshot?: Component;
}

/**
 * What a COMPONENT_HISTORY_TREE tab holds: one history entry and the Component as it was then. The
 * tab's id is the entry's, so each version opened gets its own read-only tab.
 */
export interface ComponentHistorySnapshot {
    id: number;
    // The entry's position in the history, 1 for the first; shown in the tab label.
    revision: number;
    version: ComponentVersion;
    component: Component;
}
